import React, { FC, Fragment, useEffect, useState } from 'react';
import Grid from '@mui/material/Grid';
import Material from 'components/property-settings/material/material';
import Shade from 'components/property-settings/shade/shade';
import ContactType from 'components/property-settings/contact-type/contact-type';
import OcclusionType from 'components/property-settings/occlusion-type/occlusion-type';
import MetalDesign from 'components/property-settings/metal-design/metal-design';
import PonticDesign from 'components/property-settings/pontic-design/pontic-design';
import Retainment from 'components/property-settings/retainment/retainment';
import ImplantSteps from 'components/property-settings/implant-steps/implant-steps';
import DentureSteps from 'components/property-settings/denture-steps/denture-steps';
import { Box } from '@mui/material';
import BitetrayMaterial from 'components/property-settings/bitetray-material/bitetray-material';
import { SimpleConstructionProperty } from 'models/order-input/simple-order';
import { ConstructionPropertyType } from 'models/order-input/shared';

type ConstructionPropertiesProps = {
    properties: ConstructionPropertyType[];
    onSettingsChange?: (settings: SimpleConstructionProperty) => void;
    availableMaterials: string[];
    settings?: SimpleConstructionProperty;
};
const ConstructionProperties: FC<ConstructionPropertiesProps> = ({
    properties,
    onSettingsChange,
    availableMaterials,
    settings,
}) => {
    const [currentSettings, setCurrentSettings] = useState<SimpleConstructionProperty>(
        {} as SimpleConstructionProperty,
    );

    const onSettingsSelection = (propertyName: string, settingValue: string | boolean) => {
        const settingsObj = Object.assign(currentSettings, { [propertyName]: settingValue });
        setCurrentSettings(settingsObj);
        if (onSettingsChange) {
            onSettingsChange({ ...settingsObj });
        }
    };

    useEffect(() => {
        const defaultSettings = {} as SimpleConstructionProperty;
        if (properties.includes(ConstructionPropertyType.SHADE)) {
            defaultSettings.shade = 'Vita Classic';
        }
        if (properties.includes(ConstructionPropertyType.CONTACT_TYPE)) {
            defaultSettings.contactType = 'NORMAL';
        }
        if (properties.includes(ConstructionPropertyType.METAL_DESIGN)) {
            defaultSettings.design = 'NO_METAL_MARGIN';
        }
        if (properties.includes(ConstructionPropertyType.PONTIC_DESIGN)) {
            defaultSettings.ponticDesign = 'BULLET';
        }
        if (properties.includes(ConstructionPropertyType.OCCLUSION_TYPE)) {
            defaultSettings.occlusion = 'NORMAL';
        }
        if (settings) {
            setCurrentSettings({ ...defaultSettings, ...settings });
        } else {
            setCurrentSettings(defaultSettings);
        }
        if (onSettingsChange) {
            onSettingsChange({ ...defaultSettings, ...settings });
        }
    }, [properties, settings]);

    return (
        <Fragment>
            <Box flexGrow={1}>
                <Grid container spacing={2}>
                    {properties.includes(ConstructionPropertyType.SHADE) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <Shade
                                shade={currentSettings?.shade}
                                color={currentSettings?.color}
                                colorSampling={currentSettings?.colorSampling}
                                handleSettings={onSettingsSelection}
                                isRequired
                            />
                        </Grid>
                    ) : undefined}
                    {properties.includes(ConstructionPropertyType.MATERIAL) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <Material
                                material={currentSettings?.material}
                                otherMaterialValue={currentSettings?.otherMaterial}
                                handleSettings={onSettingsSelection}
                                isRequired
                                options={availableMaterials}
                            />
                        </Grid>
                    ) : undefined}
                    {properties.includes(ConstructionPropertyType.BITETRAY_MATERIAL) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <BitetrayMaterial
                                material={currentSettings?.material}
                                softInside={currentSettings?.softInside}
                                handleSettings={onSettingsSelection}
                                isRequired
                            />
                        </Grid>
                    ) : undefined}
                    {properties.includes(ConstructionPropertyType.IMPLANT_STEPS) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ImplantSteps
                                implantSteps={currentSettings?.implantSteps}
                                handleSettings={onSettingsSelection}
                                isRequired
                            />
                        </Grid>
                    ) : undefined}
                    {properties.includes(ConstructionPropertyType.RETAINMENT) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <Retainment
                                retainment={currentSettings?.retainment}
                                handleSettings={onSettingsSelection}
                                isRequired
                            />
                        </Grid>
                    ) : undefined}
                    {properties.includes(ConstructionPropertyType.CONTACT_TYPE) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <ContactType
                                contactType={currentSettings?.contactType}
                                handleSettings={onSettingsSelection}
                            />
                        </Grid>
                    ) : undefined}
                    {properties.includes(ConstructionPropertyType.METAL_DESIGN) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <MetalDesign design={currentSettings?.design} handleSettings={onSettingsSelection} />
                        </Grid>
                    ) : undefined}
                    {properties.includes(ConstructionPropertyType.PONTIC_DESIGN) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <PonticDesign
                                ponticDesign={currentSettings?.ponticDesign}
                                handleSettings={onSettingsSelection}
                            />
                        </Grid>
                    ) : undefined}
                    {properties.includes(ConstructionPropertyType.OCCLUSION_TYPE) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <OcclusionType
                                occlusion={currentSettings?.occlusion}
                                handleSettings={onSettingsSelection}
                            />
                        </Grid>
                    ) : undefined}
                    {properties.includes(ConstructionPropertyType.DENTURE_STEPS) ? (
                        <Grid item xs={12} md={6} lg={4}>
                            <DentureSteps
                                dentureSteps={currentSettings?.dentureSteps}
                                handleSettings={onSettingsSelection}
                            />
                        </Grid>
                    ) : undefined}
                </Grid>
            </Box>
        </Fragment>
    );
};

export default ConstructionProperties;
